import {
  LinkItem,
  ThumbnailOptionGroup,
  ThumbnailOptionItem
} from 'Types/Common';

import {
  EditableYarnList,
  EngineLibrariesMap,
  LibrarySpriteItem,
  PaletteLink,
  YarnCodeList,
  YarnItemDisplay
} from 'Types/Yarns';

import {
  IRoomSceneCategory
} from 'Components/Pages/Design/Rooms/Props';

import { ColorFavorite } from './ColorFavorites';

type Rgb = TrykApi.Catalog.IRgb;

export interface QueryArgs {
  designCode: string;

  colorCode?: string;
  refColorCode?: string;
  refLibs?: string;
  yarnCodes?: YarnCodeList;
  textureId?: number;
  rgbs?: Rgb[];
  clutColorwayId?: string;
  installId?: number;
  roomId?: number;
  viewMode?: ViewMode;
  mutedIndices?: number[];
  rugWidth?: number;
  rugHeight?: number;
  rugShape?: string;
  rotation?: number;
  position?: SurfacePosition;
  folderId?: number;
}

export type SurfacePosition = {
  translate: { x: number, y: number };
  textureOrigin: { x: number, y: number };
};

export type RecolorMode = 'none' | 'yarns' | 'rgb';

export type ViewMode = '2d' | '3d';

export function isViewMode(mode: string): boolean {
  return ['2d', '3d'].indexOf(mode) !== -1;
}

export type CustomRugInfo = {
  rugWidth: number;
  rugHeight: number;
  rugShape: string;
};

export interface Params {
  colorway: TrykApi.Catalog.IColorway;
  customRug: CustomRugInfo;
  customRugVisible: boolean;
  clutColorwayId?: string;
  mutedIndices: number[];
  refLibs?: string;
  room: TrykApi.Catalog.IRoomScene;
  install: TrykApi.Catalog.IDesignInstall;
  texture?: TrykApi.Catalog.ITexture;
  viewMode: ViewMode;
  rotation: number;
  position: SurfacePosition;
  sectionIds: number[];
  folderId?: number;
}

export interface ViRRenderItem {
  surface: string;
  textureUrl: string;
  normalUrl?: string;
  ppi: number;
  repeatX: boolean;
  repeatY: boolean;
  rotate: number;
  materialPropertiesUrl?: string;
  repeatSize?: TrykApi.Catalog.ITileSize;
}

export interface ViRSettings {
  roomId: number;
  items: ViRRenderItem[];
  roomInfo?: TrykApi.Images.IRoomInfo;
  roomInfoLoading: boolean;
}

export interface ImageSettings {
  url: string;
  disableZoom: boolean;
  disableContextMenu: boolean;
  virSettings: ViRSettings;
}

export interface PatternOptions {
  colorways: ThumbnailOptionItem[];
  installs: ThumbnailOptionItem[];
  installsBySize: ThumbnailOptionGroup[];
  rooms: ThumbnailOptionItem[];
  roomCategories: IRoomSceneCategory[];
  roomBrandMap: _.Dictionary<number[]>;
  brandNameMap: _.Dictionary<string>;
  textures: ThumbnailOptionItem[];
}

export interface RecolorBase {
  loading: boolean;
  designCode: string;
  refColorCode: string;
  viewMode: ViewMode;
  roomId: number;
  installId: number;
  textureId: number;
  customRug: CustomRugInfo;
  rotation: number;
  position: SurfacePosition;
  folderId?: number;
}

export interface RecolorRgb extends RecolorBase {
  rgbs: Rgb[];
}

export interface RecolorYarns extends RecolorBase {
  paletteLinks: PaletteLink[];
  yarns: EditableYarnList;
  yarnUsages: TrykApi.Catalog.IColorwayYarnUsage[];
  allowCustomTwists: boolean;
  useLockedRecoloring: boolean;
  libraries: LibrarySpriteItem[];
  searchYarns: YarnItemDisplay[];
  disableYarnRecoloring: boolean;
  canGenerateRecolorables: boolean;
  libraryMap: EngineLibrariesMap;
}

export interface RecolorManagement {
  generateRecolorables: () => void;
  checkRecolorables: () => void;
  recolorablesGenerating: boolean;
}

export interface RecolorConfig {
  enabled: boolean;
  mode: RecolorMode;
  rgb: RecolorRgb;
  yarns: RecolorYarns;
  tabName: string;
}

export interface RecolorColorFavorites {
  colorFavorites: ColorFavorite[];
  selectedColorFavorite: string;
  readonly loadColorFavorites: () => void;
  setColorFavorites: (colorFavorites: ColorFavorite[]) => Promise<boolean>;
  setSelectedColorFavorite: (selectedColorFavorite: string) => Promise<boolean>;
  isLoggedIn: boolean;
  engineId: number;
}

export interface AreaRugConfig {
  params: Params;
}

export interface PatternInfo {
  primaryHeading: string;
  secondaryHeading?: string;
  items: PatternInfoItem[];
  links: LinkItem[];
  notices: string[];
  exclusiveDesign: boolean;
  lastGenerated: Date;
  isConcept: boolean;
  brandId: number;
}

export interface PatternInfoItem {
  label: string;
  value: string;
}

export interface AnalyticsInfo {
  id: string;
  name: string;
  brand: string;
  category: string;
  variant: string;
  price: string;
  quantity: number;
  dimension6: string;
}
